/**
 * The signature under the box — Aaron, Oct 24, Inbox note 2.7.
 *
 * Every email a person sends through Activity ends with their signature,
 * added on the server as it leaves (`services/activity/outgoing-email.ts`),
 * so the words they type stay the only thing the thread keeps. This is what
 * they are shown while writing: the same signature, rendered for them, in the
 * same paper the settings page previews it on (`signature-paper.tsx`).
 *
 * **Folded in the Inbox's composer, whole in New email.** The composer sits
 * under the thread it answers, and four lines of letterhead there are four
 * lines of the customer's message pushed out of sight — so it is one line,
 * the signature's words run together, with **Show** to see it drawn. The New
 * email sheet has the room, and draws it whole.
 *
 * Asked once per page: a signature is a company setting, not something that
 * moves while somebody writes. Somebody with no signature sees nothing here,
 * which is what their email will carry.
 */
import { useEffect, useMemo, useState } from 'react'
import type { MySignature } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { SignaturePaper } from '../../components/signature-paper.js'
import { activityRequest } from '../api.js'

let asked: Promise<MySignature | null> | null = null

/** Tests only: forget the answer, so the next composer asks again. */
export function forgetMySignature(): void {
  asked = null
}

export function useMySignature(): MySignature | null {
  const [signature, setSignature] = useState<MySignature | null>(null)
  useEffect(() => {
    let live = true
    asked ??= activityRequest<MySignature>('/me/email-signature').catch(() => null)
    void asked.then((answer) => { if (live) setSignature(answer) })
    return () => { live = false }
  }, [])
  return signature
}

/** *Tom Reyes · Hardscape design · Susquehanna Lawn & Landscape* — a signature's words on one line. */
export function signatureLine(html: string): string {
  const marked = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|tr|td|th|li|h[1-6]|table)>/gi, '\n$&')
  const text = new DOMParser().parseFromString(marked, 'text/html').body.textContent ?? ''
  return text.split('\n').map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean).join(' · ')
}

/** The viewer's signature, as their email will end — or nothing, when it ends with none. */
export function SignatureUnder({ folded = false, className }: {
  /** One line with **Show** (the composer), or drawn whole (New email). */
  folded?: boolean
  className?: string
}) {
  const signature = useMySignature()
  const [open, setOpen] = useState(false)
  const line = useMemo(() => (signature?.html ? signatureLine(signature.html) : ''), [signature?.html])
  if (!signature?.html) return null
  const drawn = !folded || open
  return (
    <div data-signature-under className={cn('flex flex-col gap-1', className)}>
      {folded && (
        <div className="flex min-w-0 items-center gap-2 text-[11.5px]">
          <span className="shrink-0 font-semibold text-text-muted">Signature</span>
          {!open && <span className="min-w-0 truncate text-text-faint">{line}</span>}
          <button type="button" aria-expanded={open} onClick={() => setOpen((was) => !was)}
            className="ml-auto shrink-0 font-semibold text-accent hover:text-accent-dark">
            {open ? 'Hide' : 'Show'}
          </button>
        </div>
      )}
      {drawn && <SignaturePaper html={signature.html} title="Your signature" maxHeight={160} />}
      {!folded && (
        <span className="text-[11px] leading-[1.4] text-text-faint">
          Your signature — added under what you write when it’s sent
        </span>
      )}
    </div>
  )
}
